import React, { useState, useEffect } from 'react';
import './Calendar.css';
import { API_URL } from './config';

function Calendar() {
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  // No default -- the visitor must explicitly pick a location first, so
  // they never see one branch's schedule while assuming it's another's.
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/branches`)
      .then(res => res.json())
      .then(data => setBranches(data.branches || []))
      .catch(() => setBranches([]))
      .finally(() => setBranchesLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedBranchId) { setCalendarData(null); return; }
    fetchCalendarData(selectedBranchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  const fetchCalendarData = async (branchId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/calendar?branch_id=${branchId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch calendar data');
      }
      const data = await response.json();
      setCalendarData(data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getTypeClass = (type) => {
    switch(type) {
      case 'holiday':
        return 'type-holiday';
      case 'online':
        return 'type-online';
      case 'test':
      case 'project':
      case 'event':
        return 'type-special';
      case 'regular':
      default:
        return 'type-regular';
    }
  };

  const groupByTerm = (schedule) => {
    const terms = [
      { name: 'Term 1', items: [] },
      { name: 'Term 2', items: [] },
      { name: 'Term 3', items: [] }
    ];
    let currentTermIndex = 0;

    schedule.forEach(item => {
      // Remove term info from dateLabel if present
      const cleanDateLabel = item.dateLabel.replace(/\s*\(Term \d+ (Start|End)\)/, '');
      const cleanItem = { ...item, dateLabel: cleanDateLabel };

      if (item.termInfo && item.termInfo.includes('Start')) {
        // Switch to the next term when we see a "Term X Start"
        const termNumber = item.termInfo.match(/Term (\d+)/)?.[1];
        if (termNumber) {
          currentTermIndex = parseInt(termNumber) - 1;
        }
      }

      // Add item to current term
      if (currentTermIndex >= 0 && currentTermIndex < 3) {
        terms[currentTermIndex].items.push(cleanItem);
      }
    });

    // Filter out any empty terms
    return terms.filter(term => term.items.length > 0);
  };

  const branchPicker = (
    <div className="calendar-branch-picker">
      <label htmlFor="calendar-branch-select">Select a location to see its calendar:</label>
      <select
        id="calendar-branch-select"
        value={selectedBranchId}
        onChange={e => setSelectedBranchId(e.target.value)}
      >
        <option value="">Choose a location…</option>
        {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </div>
  );

  if (branchesLoading) {
    return <div className="calendar-loading">Loading…</div>;
  }

  if (!selectedBranchId) {
    return (
      <div className="calendar-container">
        <h1 className="calendar-title">Calendar</h1>
        {branchPicker}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="calendar-container">
        <h1 className="calendar-title">Calendar</h1>
        {branchPicker}
        <div className="calendar-loading">Loading calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-container">
        <h1 className="calendar-title">Calendar</h1>
        {branchPicker}
        <div className="calendar-error">Error: {error}</div>
      </div>
    );
  }

  if (!calendarData) {
    return (
      <div className="calendar-container">
        <h1 className="calendar-title">Calendar</h1>
        {branchPicker}
        <div className="calendar-error">No calendar data available</div>
      </div>
    );
  }

  const terms = groupByTerm(calendarData.schedule);

  return (
    <div className="calendar-container">
      <h1 className="calendar-title">Calendar {calendarData.academicYear}</h1>
      {branchPicker}

      {calendarData.batches.length > 0 && (
        <div className="batches-section">
          <h2>Class Timings</h2>
          <div className="batches-grid">
            {calendarData.batches.map(b => (
              <div className="batch-card" key={b.batchKey}>
                <h3>{b.name}</h3>
                <p>{b.time}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="schedule-section">
        {terms.map((term, termIndex) => (
          <div key={termIndex} className="term-section">
            <h3 className="term-title">{term.name}</h3>
            <div className="schedule-table">
              <div className="table-header">
                <div className="header-cell week-col">Week #</div>
                <div className="header-cell date-col">Date</div>
                <div className="header-cell work-col">Work Plan</div>
              </div>
              {term.items.map((item, index) => (
                <div key={index} className={`table-row ${getTypeClass(item.type)}`}>
                  <div className="table-cell week-col">
                    {item.weekNumber !== null ? item.weekNumber : ''}
                  </div>
                  <div className="table-cell date-col">
                    {item.dateLabel}
                  </div>
                  <div className="table-cell work-col">
                    {item.description.split('\n').map((line, i) => (
                      <span key={i}>
                        {line}
                        {i < item.description.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Calendar;
